import {
  CellCtrl,
  CellPosition,
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellComp,
  IRowNode,
  RowContainerComp,
} from 'ag-grid-community';
import { find, get, includes, isEmpty, isEqual, isNil, pick } from 'lodash';
import { Observable, observableIIF, observableOf, rx } from '../rx/rx-wrapper';
import { IBaseEntityType } from '../types/entities/base.type';
import { NO_SELECTED_BG_CLASS } from '../constants/ag-grid.constants';

export const getDisplayedItemsIds = (gridApi: GridApi): string[] => {
  if (!gridApi) return [];

  const displayedItemsIds: string[] = [];
  gridApi.forEachNodeAfterFilterAndSort((rowNode: IRowNode) => displayedItemsIds.push(rowNode.id));

  return displayedItemsIds;
};

/**
 * make sure grid is ready
 * @param params grid api and grid ready observable
 * @returns grid api reference
 */
export const ensureGridApiExist = async (params: {
  gridApi: GridApi;
  gridReady$: Observable<GridReadyEvent>;
}): Promise<GridApi> =>
  observableIIF(
    () => !params.gridApi,
    params.gridReady$.pipe(rx.map((event) => event.api)),
    observableOf(params.gridApi),
  )
    .pipe(rx.take(1))
    .toPromise();

/**
 * set grid row data safety
 * will set grid data after grid api have any value
 * @param data new grid data
 * @param options grid api and grid ready observable
 */
export const setGridRowDataSafety = async (
  data: IBaseEntityType[],
  options: {
    gridApi: GridApi;
    gridReady$: Observable<GridReadyEvent>;
  },
): Promise<void> => {
  const gridApi = await ensureGridApiExist(pick(options, ['gridApi', 'gridReady$']));
  gridApi.setGridOption('rowData', data);
};

/**
 * Get the currently focused cell in the grid.
 * @param gridApi the grid API instance to retrieve the focused cell from
 * @returns the currently focused cell position, or undefined if no cell is focused
 */
export const getFocusedCell = (gridApi: GridApi): CellPosition | undefined => {
  if (!gridApi) {
    return undefined;
  }

  return gridApi.getFocusedCell() ?? undefined;
};

/**
 * Get the cell controller corresponding to the given cell position from the list of cell controllers.
 *
 * @param cellPosition the position of the cell to retrieve the controller for
 * @param cellCtrlList the list of cell controllers to search within
 * @returns the cell controller corresponding to the given cell position, or null if not found
 */
export const getGridCellCtrl = (cellPosition: CellPosition, cellCtrlList: CellCtrl[]): CellCtrl => {
  if (isNil(cellPosition) || isNil(cellPosition.column) || isEmpty(cellCtrlList)) {
    return null;
  }

  return find(
    cellCtrlList,
    (cellCtrl) =>
      isEqual(cellCtrl.getColumn().getColId(), cellPosition.column.getColId()) &&
      isEqual(cellCtrl.getCellPosition().rowIndex, cellPosition.rowIndex),
  );
};

/**
 * check is col cell clickable
 * @param colDef col definition
 * @returns [is cell clickable, colId]
 */
export const isCellClickable = (
  colDef: ColDef,
  enableColumnSelectCols: string[],
): [boolean, string] => {
  const colId = colDef?.field;

  return [includes(enableColumnSelectCols, colId), colId];
};

/**
 * Toggle the CSS class on the cell corresponding to the given cell position.
 *
 * !!!CAUTION: This function will not work if the row is not currently rendered in the DOM.
 *
 * @param params the parameters for toggling the cell class, including the cell position,
 * the list of cell controllers, the class name to toggle, and the toggle state.
 * @returns void
 */
export const toggleCellClass = (params: {
  cellPosition: CellPosition;
  cellCtrlList: CellCtrl[];
  className: string;
  on: boolean;
}): void => {
  const { cellPosition, cellCtrlList, className, on } = params;

  if (isNil(cellPosition) || isNil(cellPosition.column) || isEmpty(cellCtrlList)) {
    return;
  }

  const targetCtrl = getGridCellCtrl(cellPosition, cellCtrlList);

  // can't find any target ctrl just return
  if (!targetCtrl) {
    return;
  }

  (targetCtrl['cellComp'] as ICellComp).addOrRemoveCssClass(className, on);
};

/**
 * Toggle the CSS class on the row corresponding to the given cell position.
 *
 * !!!CAUTION: This function will not work if the row is not currently rendered in the DOM.
 *
 * @param params the parameters for toggling the row class, including the cell position,
 * the list of cell controllers, the class name to toggle, and the toggle state.
 * @returns void
 */
export const toggleRowClass = (params: {
  cellPosition: CellPosition;
  cellCtrlList: CellCtrl[];
  className: string;
  on: boolean;
}): void => {
  const { cellPosition, cellCtrlList, className, on } = params;

  if (isNil(cellPosition) || isNil(cellPosition.column) || isEmpty(cellCtrlList)) {
    return;
  }

  const targetCtrl = getGridCellCtrl(cellPosition, cellCtrlList);
  const rowCtrl = targetCtrl?.getRowCtrl();

  if (isNil(rowCtrl)) {
    return;
  }

  const rowComp = get(rowCtrl, 'centerGui.rowComp');

  if (!rowComp) {
    return;
  }

  (rowComp as RowContainerComp).addOrRemoveCssClass(className, on);
};

/**
 * Switch the selected row by toggling the CSS class on the old and new cell positions.
 *
 * !!!CAUTION: This function will not work if the row is not currently rendered in the DOM.
 *
 * @param params the parameters for switching the selected row, including old and new cell positions,
 *  the list of cell controllers, and the class name to toggle.
 * @returns void
 */
export const switchSelectedRow = (params: {
  oldCellPosition: CellPosition;
  newCellPosition: CellPosition;
  cellCtrlList: CellCtrl[];
}): void => {
  const { oldCellPosition, newCellPosition, cellCtrlList } = params;

  const className = NO_SELECTED_BG_CLASS;

  // hide selected row class on old selected row
  toggleRowClass({
    className,
    cellPosition: oldCellPosition,
    cellCtrlList: cellCtrlList,
    on: true,
  });

  // show selected row class on new selected row
  toggleRowClass({
    className,
    cellPosition: newCellPosition,
    cellCtrlList: cellCtrlList,
    on: false,
  });
};

export const ensureSelectedNodeVisible = (params: { api: GridApi }): void => {
  const { api } = params;

  if (!api) {
    return;
  }

  const selectedNode = api?.getSelectedNodes()[0];

  if (!selectedNode) {
    return;
  }
  api.ensureNodeVisible(selectedNode);
};

export const isSameCtrl = (ctrl1: CellCtrl, ctrl2: CellCtrl): boolean => {
  if (isNil(ctrl1) || isNil(ctrl2)) {
    return false;
  }

  return ctrl1 === ctrl2;
};

/**
 * reset scroll position back to top
 * @param params
 * @returns
 */
export const ensureTopRowVisible = (params: { api: GridApi }): void => {
  const { api } = params;

  if (!api) {
    return;
  }

  api.ensureIndexVisible(0, 'top');
};
